import { create } from "zustand";
import {
  getEmailType,
  register,
  SIGNUP_FLOW_EVENTS,
  track,
} from "@/features/analytics";
import { useAuthStore } from "@/features/authentication";
import { type BrandPrefill, resolveBrandPrefill } from "../utils/brand-prefill";
import { getBusinessDomain } from "../utils/email-domain";

// The signup email reaches the funnel via session storage (the real `/sign-up`
// hand-off), never the URL — it's PII that would leak via history/referrer.
const EMAIL_KEY = "funnel_signup_email";

/** Store (or clear) the signup email for the funnel's brand prefill. */
export const setSignupEmail = (email: string | null) => {
  try {
    if (email) sessionStorage.setItem(EMAIL_KEY, email);
    else sessionStorage.removeItem(EMAIL_KEY);
  } catch {
    // no-op
  }
  const domain = email ? getBusinessDomain(email) : null;
  useBrandPrefillStore
    .getState()
    .setBrand(domain ? resolveBrandPrefill(domain) : null);

  if (email) {
    // Work-email detection step of the [New Signup Flow] enrichment funnel.
    // `email type` is also registered so it rides the rest of the funnel.
    const emailType = getEmailType(email);
    register({ "email type": emailType });
    track(SIGNUP_FLOW_EVENTS.DETECT_EMAIL_DOMAIN, {
      "email type": emailType,
      "domain detected": Boolean(domain),
      "enrichment status": domain ? "domain only" : "none",
    });
  }
};

const readSignupEmail = (): string => {
  const signedInEmail = useAuthStore.getState().user?.email;
  if (signedInEmail) return signedInEmail;
  try {
    return sessionStorage.getItem(EMAIL_KEY) || "";
  } catch {
    return "";
  }
};

const resolveInitialBrand = (): BrandPrefill | null => {
  const domain = getBusinessDomain(readSignupEmail());
  return domain ? resolveBrandPrefill(domain) : null;
};

interface BrandPrefillState {
  /** Null when the email is personal/generic or unknown — nothing prefills. */
  brand: BrandPrefill | null;
  setBrand: (brand: BrandPrefill | null) => void;
}

export const useBrandPrefillStore = create<BrandPrefillState>((set) => ({
  brand: resolveInitialBrand(),
  setBrand: (brand) => set({ brand }),
}));

/**
 * Re-derives the prefill from the signed-in user's email (or the stored
 * signup email), dropping any brand a previous funnel run selected. Called on
 * each platform-funnel open so an abandoned run can't leak its brand into the
 * next one.
 */
export const resetBrandPrefill = () => {
  useBrandPrefillStore.getState().setBrand(resolveInitialBrand());
};
